import OutlinedCard from '@/Components/Cards/OutlinedCard.jsx';
import Modal from '@/Components/Common/Modal.jsx';
import {theme_air_water} from '@/Themes/Mistubishi/theme.js';
import {Box, Typography, List, ListItem, ListItemText, Stack, Button, ThemeProvider} from '@mui/material';
import * as React from 'react';

const PatchNoteModal = ({open, onClose}) => {
    const patchNotes = [
        {
            version: '2.0',
            date: '2024',
            type: 'major',
            title: 'SelectME 2.0 — Patch Note',
            introduction: `
            Bonjour à tous,<br/><br/>
            Le logiciel <strong>SelectME2.0</strong> évolue ✨ pour continuer à répondre <strong>au plus près de vos besoins</strong> et vous offrir une <strong>expérience toujours plus fluide, intuitive et performante</strong>.<br/><br/>
            Cette nouvelle version introduit des <strong>fonctionnalités inédites</strong>, des optimisations ciblées et des <strong>améliorations de confort d’utilisation</strong> issues de vos retours.<br/><br/>
            Découvrez sans plus attendre ce que cette mise à jour vous réserve 👇
            `,
            changes: [
                {
                    list_chip: '🏗️',
                    content: `<strong>Sélection de système cascade</strong> désormais disponible, pour des configurations encore plus complètes et modulables.`,
                    image: null,
                },
                {
                    list_chip: '🧩',
                    content: `<strong>Nouvelle page «&nbsp;Accessoires&nbsp;»</strong>&nbsp;: complétez facilement votre sélection avec les <strong>accessoires associés</strong> à vos produits.`,
                    image: null,
                },
                {
                    list_chip: '♻️',
                    content: `La modification des <strong>données énergies et émissions de CO₂ par l'utilisateur est désormais disponible</strong>, pour une meilleure personnalisation de ses calculs.`,
                    image: null,
                },
                {
                    list_chip: '⚙️',
                    content: `Les <strong>champs «&nbsp;Entreprise&nbsp;»</strong> sont maintenant <strong>modifiables dans la note de dimensionnement</strong>, afin de simplifier la gestion et la mise à jour de vos informations.`,
                    image: null,
                },
                {
                    list_chip: '🔥',
                    content: `Limitation automatique des résultats de sélection aux <strong>5 meilleures options</strong>&nbsp;: gagnez du temps et accédez directement aux propositions les plus pertinentes.`,
                    image: null,
                },
                {
                    list_chip: '🌟',
                    content: `Affichage automatique du changelog lors de la première connexion&nbsp;: découvrez instantanément toutes les nouveautés dès votre arrivée.`,
                    image: null,
                },
            ],
            fixes: [
                {
                    list_chip: '📋',
                    content: `Note de dimensionnement plus lisible et mise à jour selon les <strong>dernières exigences réglementaires</strong>, pour rester pleinement conforme.`,
                    image: null,
                },
                {
                    list_chip: '📊',
                    content: `Rectification de l'ordre d'affichage des températures dans le <strong>graphe du rapport de sélection</strong>, garantissant une lecture plus claire et logique.`,
                    image: null,
                },
                {
                    list_chip: '🌡️',
                    content: `Températures de départ d'eau prédéfinies et adaptées automatiquement à chaque émetteur&nbsp;:<br/>
<strong style="margin-left: 48px;">Radiateurs</strong> — 55°C<br/>
<strong style="margin-left: 48px;">Ventilo-convecteurs</strong> — 40°C<br/>
<strong style="margin-left: 48px;">PCBT</strong> — 35°C.`,
                    image: null,
                },
                {
                    list_chip: '📈',
                    content: `Lisibilité améliorée du graphe puissance/déperdition lors de la sélection, pour une visualisation plus nette et ergonomique.`,
                    image: null,
                },
                {
                    list_chip: '🚫',
                    content: `Suppression des sets Génération D dans les résultats de simulation pour les projets <strong>«&nbsp;Haute Température&nbsp;» (65–70°C).</strong>`,
                    image: null,
                },
            ],
            conclusion: `<br/>
Avec cette mise à jour, <strong>SelectME2.0 franchit une nouvelle étape</strong> dans sa mission : vous offrir un outil <strong>plus intelligent, plus ergonomique et plus proche de vos usages terrain</strong>.<br/><br/>
Chaque amélioration a été pensée pour <strong>simplifier votre quotidien</strong>, <strong>fiabiliser vos calculs</strong> et <strong>accélérer vos projets</strong>.<br/><br/>
🌟 Continuez à nous partager vos retours — ils inspirent directement les évolutions de demain&nbsp;!<br/><br/>
✨ Et ce n’est pas tout&nbsp;! D’autres améliorations sont <strong>déjà en cours de développement</strong> et seront disponibles <strong>d’ici la fin de l’année</strong>…<br/><br/>
Restez connectés&nbsp;! 💫<br/><br/>
<span style="display: block; text-align: center;">En vous souhaitant d’excellentes ventes !</span>
<span style="display: block; text-align: center; font-weight: bold; color: ${theme_air_water.palette.primary.main}">L’équipe Marketing Résidentiel</span>
            `,
        },
    ];

    const getChipColor = (type) => {
        switch (type) {
            case 'major':
                return 'primary';
            case 'minor':
                return 'secondary';
            case 'patch':
                return 'default';
            default:
                return 'default';
        }
    };

    return (
        <ThemeProvider theme={theme_air_water}>
            <Modal
                maxWidth={'md'}
                open={open}
                onClose={() => {}} // Désactive la fermeture en cliquant à l'extérieur
                scroll={'paper'}
                fullWidth={true}
                disableEscapeKeyDown={true} // Désactive la fermeture avec la touche Échap
                title={<>Nouveautés</>}
                TitleProps={{
                    component: 'div',
                    variant: 'h2',
                    sx: {
                        fontWeight: 400,
                        p: 2,
                        mb: 0,
                        textAlign: 'center',
                    },
                }}
            >
                <Stack spacing={3} sx={{my: 2}}>
                    {patchNotes.map((patch, index) => (
                        <OutlinedCard key={index} title={patch.title}>
                            <Stack spacing={2} sx={{mt: 2}}>
                                {patch.introduction && (
                                    <Typography
                                        component="div"
                                        sx={{'& p': {m: 0}, lineHeight: 1.25}}
                                        dangerouslySetInnerHTML={{__html: patch.introduction}}
                                    />
                                )}
                                {patch.changes && patch.changes.length > 0 && (
                                    <Box>
                                        <Typography variant="subtitle2" color="primary" gutterBottom>
                                            ✨ Nouveautés
                                        </Typography>
                                        <List dense sx={{p: 0}}>
                                            {patch.changes.map((change, changeIndex) => (
                                                <ListItem
                                                    key={changeIndex}
                                                    disableGutters
                                                    sx={{
                                                        mt: 2,
                                                        p: 0,
                                                        flexDirection: 'column',
                                                        alignItems: 'flex-start',
                                                    }}
                                                >
                                                    <Box
                                                        sx={{display: 'flex', alignItems: 'flex-start', width: '100%'}}
                                                    >
                                                        <Box>{change?.list_chip ?? '•'}&nbsp;</Box>
                                                        {typeof change === 'string' ? (
                                                            <Typography sx={{m: 0, lineHeight: 1.25}}>
                                                                {change}
                                                            </Typography>
                                                        ) : (
                                                            <Typography
                                                                component="div"
                                                                sx={{m: 0, lineHeight: 1.25}}
                                                                dangerouslySetInnerHTML={{__html: change.content}}
                                                            />
                                                        )}
                                                    </Box>
                                                    {typeof change === 'object' && change.image && (
                                                        <Box sx={{mt: 1, ml: 2, width: '100%', maxWidth: 400}}>
                                                            <img
                                                                src={change.image}
                                                                alt={
                                                                    typeof change.content === 'string'
                                                                        ? change.content
                                                                        : 'illustration'
                                                                }
                                                                style={{
                                                                    width: '100%',
                                                                    height: 'auto',
                                                                    borderRadius: '8px',
                                                                    border: '1px solid rgba(0, 0, 0, 0.12)',
                                                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                                                }}
                                                            />
                                                        </Box>
                                                    )}
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                )}

                                {patch.fixes && patch.fixes.length > 0 && (
                                    <Box>
                                        <Typography variant="subtitle2" gutterBottom>
                                            🔧 Corrections
                                        </Typography>
                                        <List dense sx={{p: 0}}>
                                            {patch.fixes.map((fix, fixIndex) => (
                                                <ListItem
                                                    key={fixIndex}
                                                    disableGutters
                                                    sx={{
                                                        mt: 2,
                                                        p: 0,
                                                        flexDirection: 'column',
                                                        alignItems: 'flex-start',
                                                        justifyContent: 'flex-start',
                                                    }}
                                                >
                                                    <Box
                                                        sx={{display: 'flex', alignItems: 'flex-start', width: '100%'}}
                                                    >
                                                        <Box>{fix?.list_chip ?? '•'}&nbsp;</Box>
                                                        {typeof fix === 'string' ? (
                                                            <Typography sx={{m: 0, lineHeight: 1.25}}>{fix}</Typography>
                                                        ) : (
                                                            <Typography
                                                                component="div"
                                                                sx={{m: 0, lineHeight: 1.25}}
                                                                dangerouslySetInnerHTML={{__html: fix.content}}
                                                            />
                                                        )}
                                                    </Box>
                                                    {typeof fix === 'object' && fix.image && (
                                                        <Box sx={{mt: 1, ml: 2, width: '100%', maxWidth: 400}}>
                                                            <img
                                                                src={fix.image}
                                                                alt={
                                                                    typeof fix.content === 'string'
                                                                        ? fix.content
                                                                        : 'illustration'
                                                                }
                                                                style={{
                                                                    width: '100%',
                                                                    height: 'auto',
                                                                    borderRadius: '8px',
                                                                    border: '1px solid rgba(0, 0, 0, 0.12)',
                                                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                                                }}
                                                            />
                                                        </Box>
                                                    )}
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                )}

                                {patch.conclusion && (
                                    <Typography
                                        component="div"
                                        sx={{mt: 1.5, lineHeight: 1.25}}
                                        dangerouslySetInnerHTML={{__html: patch.conclusion}}
                                    />
                                )}
                            </Stack>
                        </OutlinedCard>
                    ))}

                    {/* Bouton de validation */}
                    <Box sx={{display: 'flex', justifyContent: 'center', pt: 2}}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={onClose}
                            sx={{
                                minWidth: 200,
                                py: 1.5,
                                fontSize: '1.1rem',
                                fontWeight: 600,
                            }}
                        >
                            J'ai pris connaissance des modifications
                        </Button>
                    </Box>
                </Stack>
            </Modal>
        </ThemeProvider>
    );
};

export default PatchNoteModal;
